import { css, html, nothing, TemplateResult } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { connect } from 'pwa-helpers';
import './wish-list-dropdown-options';
import emit from '../../../common/events';
import { BaseComponent } from '../../base-component/base-component';
import { ProductCardData } from '../../product-card/product-card-display';
import { WishList } from '../interfaces/wish-list-items.interface';
import { booleanConverter } from '../../../common/converters';
import { smallSpinner } from '../../../common/constants/api-constants';
import { getProductFromWishList } from '../utils/wish-list-product.util';
import { wishListDropdownButtons } from '../utils/wish-list.util';
import { WishListProductAction } from '../interfaces/wish-list-update-product.interface';
import { UserProfile } from '../../user-profile/user-profile.interface';
import { WishListAnalytics } from '../utils/wish-list-analytics.util';
import { selectUser } from '../../user-profile/user-profile.selectors';
import store from '../../../store/store';
import { mapWishListToWishListAnalyticsObject } from '../mapping/wish-list-analytics-mapping';

@customElement('mhecomm-wish-list-list-display')
export class WishListListDisplay extends connect(store)(BaseComponent) {
  static styles = [
    css`
      .update-product-in-wish-list-btn {
        width: 2.5rem;
      }

      .spinner-border-sm {
        width: 0.75rem !important;
        height: 0.75rem !important;
      }
    `,
  ];

  /**
   * A flag to determine if the product has already been added to the wish list.
   */
  @property({ converter: booleanConverter })
  isProductInList?: boolean = false;

  /**
   * A flag indicating if the product is being updated.
   */
  @property({ converter: booleanConverter })
  isProductUpdating?: boolean;

  /**
   * If the component is used inside an off-canvas, this should be set to the off-canvas ID.
   */
  @property()
  offCanvasId = '';

  /**
   * The product to add to a wish list.
   */
  @property({ type: Object })
  product?: ProductCardData;

  /**
   * Whether to show the action drop down button.
   */
  @property({ converter: booleanConverter })
  showActionButton = false;

  /**
   * Whether to show the update product button.
   */
  @property({ converter: booleanConverter })
  showUpdateButton = false;

  /**
   * The wish list to display.
   */
  @property({ type: Object })
  wishList?: WishList;

  /*
   * The current logged in user profile
   */
  @state()
  private user: UserProfile | undefined;

  /**
   * The analytics instance for the wish list.
   */
  private readonly wishListAnalytics: WishListAnalytics;

  constructor() {
    super();
    this.wishListAnalytics = new WishListAnalytics();
  }

  stateChanged(state: any): void {
    this.user = selectUser(state);
  }

  renderComp(): TemplateResult | typeof nothing {
    return html`
      ${this.wishList
        ? html` <div id="wish-list-list-display-${this.wishList.wishlist_id}" class="item row py-4">
            <div class="col d-flex ${this.isProductInList ? '' : 'align-items-center'}">
              <div>
                <button
                  id="wish-list-${this.wishList.wishlist_id}-view-products"
                  data-automation-id="wish-list-${this.wishList.wishlist_id}-view-products"
                  class="btn btn-link p-0 text-start"
                  @click=${this.triggerWishListReview}
                >
                  ${this.wishList.name}
                </button>
                ${this.isProductInList ? html` <div class="fs-small fst-italic text-gray">Added</div>` : ''}
              </div>
            </div>
            ${this.showActionButton || this.showUpdateButton
              ? html` <div class="col d-flex justify-content-end pe-1">
                  ${this.showActionButton ? this.getActionButton(this.wishList) : nothing}
                  ${this.showUpdateButton ? this.getUpdateButton(this.wishList) : nothing}
                </div>`
              : nothing}
          </div>`
        : nothing}
    `;
  }

  /**
   * Returns the action dropdown button.
   */
  private getActionButton(wishList: WishList): TemplateResult {
    return html`
      <div class="${this.showUpdateButton ? 'me-2' : ''}">
        <mhecomm-wish-list-dropdown-options
          .dropDownItems=${wishListDropdownButtons()}
          .dropDownView=${true}
          .offCanvasId=${this.offCanvasId}
          .wishList=${wishList}
        ></mhecomm-wish-list-dropdown-options>
      </div>
    `;
  }

  /**
   * Returns the update button.
   *
   * @param wishList
   */
  private getUpdateButton(wishList: WishList): TemplateResult {
    return html`
      <div>
        <button
          id="update-product-in-wish-list-btn-${wishList.wishlist_id}"
          data-automation-id="update-product-in-wish-list-btn-${wishList.wishlist_id}"
          type="button"
          class="btn update-product-in-wish-list-btn ${this.getButtonClass()}"
          aria-label="${this.getButtonAriaLabel()}"
          ?disabled=${this.isProductUpdating}
          @click=${this.triggerProductUpdate}
        >
          ${this.isProductUpdating ? smallSpinner() : this.isProductInList ? '-' : '+'}
        </button>
      </div>
    `;
  }

  /**
   * Returns the button aria label.
   */
  private getButtonAriaLabel(): string {
    return this.isProductInList
      ? `Remove product ${this.product?.title} from wish list ${this.wishList?.name}`
      : `Add product ${this.product?.title} to wish list ${this.wishList?.name}`;
  }

  /**
   * Returns the button class based on whether the product is in the wish list.
   */
  private getButtonClass(): string {
    return `${this.isProductInList ? 'btn-primary' : 'btn-outline-primary'}`;
  }

  /**
   * Triggers the event to review a wish list.
   */
  private triggerWishListReview(event: Event): void {
    event.preventDefault();

    // to trigger the event listener
    this.wishListAnalytics.wishListReview(this, this.wishList!, this.offCanvasId);
    // to emit event and push to datalayer
    this.wishListAnalytics.wishListItemsOffCanvasView(this, this.wishList!);
  }

  /**
   * Triggers the event when updating a product on a wish list.
   */
  private triggerProductUpdate(event: Event): void {
    event.preventDefault();
    emit({
      element: this,
      name: `mhecomm-wish-list-${this.isProductInList ? 'remove' : 'add'}-product`,
      detail: {
        eventName: `mhecomm-wish-list-${this.isProductInList ? 'remove' : 'add'}-product`,
        product: this.product,
        wishList: this.wishList!,
        wishListItemId: getProductFromWishList(this.product, this.wishList)?.wishlist_item_id,
        url: window.location.href,
        getElement: () => this.shadowRoot?.querySelector('.update-product-in-wish-list-btn') ?? this,
        label: `${this.isProductInList ? 'remove' : 'add'}-product`,
        comp: 'wish-list',
      },
      dataLayer: false,
    });
    this.captureAnalytics(
      this.isProductInList ? WishListProductAction.REMOVE : WishListProductAction.ADD,
      this,
      this.product!,
      this.wishList!
    );
  }

  private captureAnalytics(
    action: WishListProductAction,
    element: HTMLElement,
    product: ProductCardData,
    wishList: WishList
  ) {
    switch (action) {
      case WishListProductAction.ADD:
        this.wishListAnalytics.addProductToWishList(element, wishList, this.user!, product);
        break;
      case WishListProductAction.REMOVE:
        this.wishListAnalytics.removeProductFromWishList(element, wishList, this.user!, product);
        break;
    }
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'mhecomm-wish-list-list-display': WishListListDisplay;
  }
}
